import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    computed,
    signal,
    effect,
    untracked,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastService } from '@app/core/services/toast.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import {
    CarrierDeliveryNoteInPool,
    LoginOrganizationSite,
    OrganizationSite,
} from 'api/models';
import {
    DeliveryNoteBundlesService,
    DeliveryNotesService,
    PlatformService,
} from 'api/services';
import { NgxSpinnerService } from 'ngx-spinner';
import { lastValueFrom, ReplaySubject, Subject } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

// Import configurable view dependencies
import {
    ViewConfig,
    ViewType,
    UserRole,
} from '@app/shared/models/view-config.interface';
import {
    getCurrentOrganizationSiteKey,
    setSharePermissionsDialogOpen,
} from '@app/stores/appStore';
import { ViewConfigurationService } from '@app/shared/services/view-config.service';
import {
    getSelectedItems,
    clearSelection,
    setSelection,
    setContextData,
    SelectableItem,
} from '@app/stores/selectionStore';

// Import data stores like configurable view
import {
    getDeliveryNotes,
    isLoading as isDeliveryNotesLoading,
    getError as getDeliveryNotesError,
} from '@app/stores/deliveryNotesStore';
import {
    getFolders,
    isFoldersLoading,
    getFoldersError,
} from '@app/stores/foldersStore';

// Import view configs
import { SharePermissionsBundlesConfig } from '@app/shared/services/view-configs/carrier/share-permissions-bundles-config';
import { SharePermissionsDeliveryNotesConfig } from '@app/shared/services/view-configs/carrier/share-permissions-deliverynotes-config';
import { CarrierViewActionsService } from '@app/shared/services/view-configs/carrier-view-actions.service';

// Import paginated services
import { CarrierFoldersPaginatedService } from '@app/carrier/services/carrier-folders-paginated.service';
import { CarrierDeliveryNotesPaginatedService } from '@app/carrier/services/carrier-delivery-notes-paginated.service';

// Import search store functions
import { updateCurrentViewType } from '@app/stores/searchStore';

export type SharePermissionsDialogProps = {
    organizationSite: LoginOrganizationSite;
    deliveryNoteBundles?: any[];
    deliveryNotes?: CarrierDeliveryNoteInPool[];
    initialView: 'bundles' | 'deliverySlip' | 'notes';
};

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-share-permissions-dialog',
    templateUrl: './share-permissions-dialog.component.html',
    styleUrls: ['./share-permissions-dialog.component.scss'],
    standalone: false,
})
export class SharePermissionsDialogComponent implements OnInit, OnDestroy {
    organizationSiteKey: string;

    // Dialog-specific properties
    targetSites: OrganizationSite[] = [];
    showSuccessScreen = false;
    sharedDeliveryNotes: any[] = [];
    orgCtrl: FormControl = new FormControl();
    orgFilterCtrl: FormControl = new FormControl();
    filteredOrgs: ReplaySubject<OrganizationSite[]> = new ReplaySubject<
        OrganizationSite[]
    >(1);
    protected _onDestroy = new Subject<void>();

    // Configurable view logic (copied from ConfigurableViewComponent)
    userRole: UserRole = 'carrier';

    // Signals for reactive state (copied from configurable view)
    currentViewType = signal<ViewType>('folders');
    currentConfig = signal<ViewConfig | null>(null);
    organizationSiteKey$ = getCurrentOrganizationSiteKey;
    selectedItems = getSelectedItems;

    // Data computed from stores (copied from configurable view)
    data = computed(() => {
        const config = this.currentConfig();

        if (config?.type === 'deliverynotes') {
            return getDeliveryNotes() as SelectableItem[];
        } else if (config?.type === 'folders') {
            return getFolders() as SelectableItem[];
        }
        return [];
    });

    loading = computed(() => {
        const config = this.currentConfig();
        if (config?.type === 'deliverynotes') {
            return isDeliveryNotesLoading();
        } else if (config?.type === 'folders') {
            return isFoldersLoading();
        }
        return false;
    });

    error = computed(() => {
        const config = this.currentConfig();
        if (config?.type === 'deliverynotes') {
            return getDeliveryNotesError();
        } else if (config?.type === 'folders') {
            return getFoldersError();
        }
        return null;
    });

    // Computed values (copied from configurable view)
    availableViews = computed(() => {
        // Only show folders and delivery notes for share permissions
        return [
            { type: 'folders' as ViewType, name: 'Folders' },
            { type: 'deliverynotes' as ViewType, name: 'Delivery Notes' },
        ];
    });

    tableColumns = computed(() => {
        const config = this.currentConfig();
        if (!config) return [];
        return config.tableConfig.columns;
    });

    filterConfig = computed(() => {
        const config = this.currentConfig();
        return config ? config.filterConfig : null;
    });

    permissions = computed(() => {
        const config = this.currentConfig();
        return config ? config.permissions : null;
    });

    // View configurations
    bundlesConfig: ViewConfig;
    deliveryNotesConfig: ViewConfig;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        private data_props: SharePermissionsDialogProps,
        private deliveryNotesService: DeliveryNotesService,
        private ngxSpinnerService: NgxSpinnerService,
        private toastService: ToastService,
        private platformService: PlatformService,
        private deliveryNoteBundlesService: DeliveryNoteBundlesService,
        private translateService: TranslateService,
        private carrierActionsService: CarrierViewActionsService,
        private carrierFoldersPaginatedService: CarrierFoldersPaginatedService,
        private carrierDeliveryNotesPaginatedService: CarrierDeliveryNotesPaginatedService,
        private viewConfigurationService: ViewConfigurationService
    ) {
        this.organizationSiteKey = data_props.organizationSite._key;

        // Initialize view configurations
        this.initializeViewConfigs();

        // Set initial view based on props
        if (data_props.initialView === 'bundles') {
            this.currentViewType.set('folders');
        } else if (
            data_props.initialView === 'deliverySlip' ||
            data_props.initialView === 'notes'
        ) {
            this.currentViewType.set('deliverynotes');
        } else {
            this.currentViewType.set('folders');
        }

        // Effect to update config when view changes (copied from configurable view)
        effect(() => {
            const viewType = this.currentViewType();
            const config = this.getViewConfig(viewType);
            this.currentConfig.set(config || null);
        });

        // Effect to handle config changes and data loading (copied from configurable view)
        effect(() => {
            const config = this.currentConfig();
            const orgSiteKey = this.organizationSiteKey$();

            if (config && orgSiteKey) {
                untracked(() => {
                    this.loadViewData();
                });
            }
        });

        // Set dialog state to open
        setSharePermissionsDialogOpen(true);
    }

    async ngOnInit() {
        this.getTargetOrgas();

        // Initialize selection context (like configurable view)
        setContextData({
            viewType: this.currentViewType(),
            userRole: this.userRole,
            organizationSiteKey: this.organizationSiteKey || undefined,
        });

        // Clear any previous selection
        clearSelection();

        this.orgFilterCtrl.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => this.filterOrgs());
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();

        // Set dialog state to closed and trigger data refresh for main table
        setSharePermissionsDialogOpen(false);

        // Import and trigger data refresh for main table
        import('@app/stores/searchStore').then(
            ({ resetPagination, triggerDataRefresh }) => {
                // Reset pagination before refreshing to ensure clean state
                resetPagination();
                triggerDataRefresh();
            }
        );
    }

    private initializeViewConfigs() {
        // Initialize view configurations
        this.bundlesConfig = SharePermissionsBundlesConfig.create({
            carrierFoldersPaginatedService: this.carrierFoldersPaginatedService,
            actionsService: this.carrierActionsService,
        });

        this.deliveryNotesConfig = SharePermissionsDeliveryNotesConfig.create({
            carrierDeliveryNotesPaginatedService:
                this.carrierDeliveryNotesPaginatedService,
            actionsService: this.carrierActionsService,
        });
    }

    private getViewConfig(viewType: ViewType): ViewConfig | null {
        switch (viewType) {
            case 'folders':
                return this.bundlesConfig;
            case 'deliverynotes':
                return this.deliveryNotesConfig;
            default:
                return null;
        }
    }

    // Data loading (copied from configurable view)
    private async loadViewData(): Promise<void> {
        const config = this.currentConfig();
        const orgSiteKey = this.organizationSiteKey$();

        if (!config || !orgSiteKey) {
            return;
        }

        try {
            // Just trigger the API call - data will be available from store (same as configurable view)
            await config.apiConfig.loadData();
        } catch (error) {
            this.toastService.error(
                this.translateService.instant('GENERAL.ERROR.LOADING_DATA')
            );
        }
    }

    // View switching - matches main app pattern
    onViewChanged(viewType: ViewType) {
        this.currentViewType.set(viewType);
        clearSelection(); // Clear selection when switching views

        // Use the same pattern as main app (paginated-table-filter component)
        const viewConfig = this.viewConfigurationService.getViewConfig(
            this.userRole,
            viewType
        );
        const searchConfig = viewConfig?.searchConfig;

        // This properly resets pagination and triggers data refresh
        updateCurrentViewType(viewType, searchConfig);
    }

    // Selection handling (copied from configurable view)
    onSelectionChange(event: { selected: SelectableItem[] }) {
        setSelection(event.selected, {
            viewType: this.currentViewType(),
            userRole: this.userRole,
            organizationSiteKey: this.organizationSiteKey || undefined,
        });
    }

    onRefresh(): void {
        this.loadViewData();
    }

    // Dialog-specific methods (keep existing logic)
    protected filterOrgs() {
        if (!this.targetSites) {
            return;
        }

        let search = this.orgFilterCtrl.value;
        if (!search) {
            this.filteredOrgs.next(this.targetSites.slice());
        } else {
            search = search.toLowerCase();
        }

        this.filteredOrgs.next(
            this.targetSites.filter(
                org => org.name.toLowerCase().indexOf(search) > -1
            )
        );
    }

    displayFn(site: OrganizationSite): string {
        return site ? site.name : '';
    }

    get amountOfSelectedItems(): number {
        const selectedItems = getSelectedItems();
        return selectedItems.length;
    }

    async getTargetOrgas() {
        this.ngxSpinnerService.show();
        let sites = await lastValueFrom(
            this.platformService.getPlatformOrganisationsSites()
        );

        sites = sites.filter(site => site.carrier);
        this.targetSites = sites.filter(
            site => site._key !== this.organizationSiteKey
        );

        this.filteredOrgs.next(this.targetSites.slice());
        this.ngxSpinnerService.hide();
    }

    async sharePermission() {
        this.ngxSpinnerService.show();

        const selectedItems = getSelectedItems();
        this.sharedDeliveryNotes = [];

        // Handle selected bundles - get all delivery notes in each bundle
        const bundleItems = selectedItems.filter(item =>
            item.hasOwnProperty('numberOfDeliveryNotes')
        );
        for (const bundle of bundleItems) {
            const bundleDetails = await lastValueFrom(
                this.deliveryNoteBundlesService.getCarrierBundle({
                    organizationSiteKey: this.organizationSiteKey,
                    deliveryNoteBundleKey: (bundle as any)._key,
                })
            );
            this.sharedDeliveryNotes.push(...bundleDetails.deliveryNotes);
        }

        // Handle selected individual delivery notes
        const deliveryNoteItems = selectedItems.filter(
            item =>
                item.hasOwnProperty('number') &&
                !item.hasOwnProperty('numberOfDeliveryNotes')
        );
        this.sharedDeliveryNotes.push(...deliveryNoteItems);

        // Remove duplicates
        const uniqueKeys = [
            ...new Set(
                this.sharedDeliveryNotes.map(entry => (entry as any)._key)
            ),
        ];

        try {
            await lastValueFrom(
                this.deliveryNotesService.shareDeliveryNotesRights({
                    organizationSiteKey: this.organizationSiteKey,
                    body: {
                        targetOrganizationSiteKey: this.orgCtrl.value._key,
                        deliveryNoteKeys: uniqueKeys,
                    },
                })
            );
            this.toastService.success(
                this.translateService.instant('SHARE-PERMISSIONS.SUCCESS')
            );

            this.sharedDeliveryNotes = [...new Set(this.sharedDeliveryNotes)];

            this.showSuccessScreen = true;
        } catch (error: HttpErrorResponse | any) {
            if (error.error.includes('already shared')) {
                this.toastService.error(
                    this.translateService.instant(
                        'SHARE-PERMISSIONS.ERROR-ALREADY-SHARED'
                    )
                );
            } else {
                this.toastService.error(
                    this.translateService.instant('SHARE-PERMISSIONS.ERROR')
                );
            }
        } finally {
            this.ngxSpinnerService.hide();
        }
    }
}
